<!--
  Field Calculation Modal component.
-->
<template>
  <ValidationObserver v-slot="{ handleSubmit }" ref="fieldCalculationModal">
    <b-modal id="fieldCalculationModal" v-model="fieldCalculationData.modalShown" title="Configure Field Calculation"
      :hide-footer="control.loading" size="xl" scrollable @ok="handleSubmit(onSubmit)" @show="onShow"
      @hidden="hideFieldCalculationModal">
      <content-spinner :show="showFormSpinner" :text="formSpinnerText" />
      <b-form v-show="!showFormSpinner" @submit.prevent="handleSubmit(onSubmit)">
        <div class="col-sm-12 row">
          <div class="col">
            <b-form-group>
              <template #label>
                Field Label
              </template>
              <b-input id="configureFieldLabel" v-model="tableField.displayName" placeholder="Field Label"
                :disabled="true" />
            </b-form-group>
          </div>
          <div class="col">
            <b-form-group label="Field Type">
              <b-input id="configureFieldType" v-model="fieldTypeCaption" placeholder="Field Type" :disabled="true" />
            </b-form-group>
          </div>
        </div>

        <div class="col-sm-12 row">
          <div class="col-sm-12">
            <b-form-group>
              <template #label>
                Calculation Type<span class="text-danger">&nbsp;*</span>
              </template>
              <div class="row">
                <div class="col">
                  <b-form-select id="configureCalculationType" v-model="calculationData.fieldCalculationType"
                    :options="calculationTypeOptions" @change="onCalculationTypeChanged" />
                </div>
                <div class="fixed text-right">
                  <!-- Clear button -->
                  <b-button id="calculationModalClearBtn" v-b-tooltip.hover class="mt-1 mw60" variant="outline-warning"
                    size="sm" :disabled="control.clearButtonDisabled" :title="control.clearButtonTitle"
                    @click.prevent="clearCalculationData(true)">
                    Clear
                  </b-button>
                </div>
              </div>
            </b-form-group>
          </div>
        </div>

        <!-- Float calculation option modals -->
        <div>
          <b-card :header="getCalculationTypeCaption()" class="mb-1">
            <div id="fieldCalculationProperties">
              <concatenation-panel
                v-if="calculationData.fieldCalculationType === entityUtils.calculationTypes.concatenation" />
              <AIChatPanel v-else-if="calculationData.fieldCalculationType === entityUtils.calculationTypes.AIChat" />
              <row-formula-panel
                v-else-if="calculationData.fieldCalculationType === entityUtils.calculationTypes.RowFormula" />
              <aggregation-panel
                v-else-if="calculationData.fieldCalculationType === entityUtils.calculationTypes.Aggregation" />
              <mdm-aggregation-panel
                v-else-if="calculationData.fieldCalculationType === entityUtils.calculationTypes.MdmAggregation" />
              <lookup-panel v-else-if="calculationData.fieldCalculationType === entityUtils.calculationTypes.lookup" />
              <!--
              />
              <allocation-block
                v-else-if="calculationData.fieldCalculationType === 'allocation'"
              -->
            </div>
          </b-card>
        </div>

        <b-button type="submit" class="d-none" />
      </b-form>

      <div slot="modal-footer" class="d-flex">
        <!-- Cancel button -->
        <b-button id="calculationModalCancelBtn" class="mt-3 ml-3 mr-3" block
          @click="$bvModal.hide('fieldCalculationModal')">
          Cancel
        </b-button>
        <!-- Apply button -->
        <b-button id="calculationModalApplyBtn" v-b-tooltip.hover class="mt-3 mr-3" variant="primary" block
          :disabled="control.applyButtonDisabled" :title="control.applyButtonTitle" @click.prevent="onSubmit">
          Apply
        </b-button>
      </div>
    </b-modal>
  </ValidationObserver>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';
import { FIELD_INITIAL_DATA, getFieldInitialData } from '@/store/modules/data-management/state';
import ContentSpinner from '@/core/components/ContentSpinner.vue';
/* Utilities */
import * as apiUtils from '@/core/utils/apiUtils';
import * as aiUtils from '@/core/utils/aiUtils';
import * as entityUtils from '@/core/utils/entityUtils';
import * as jsonUtils from '@/core/utils/jsonUtils';
/* Components */
import ConcatenationPanel from './ConcatenationPanel.vue';
import AIChatPanel from './AIChatPanel.vue';
import RowFormulaPanel from './RowFormulaPanel.vue';
import AggregationPanel from './AggregationPanel.vue';
import MdmAggregationPanel from './MdmAggregationPanel.vue';
import LookupPanel from './LookupPanel.vue';
import ConnectorViewSalesforceNew from '@/modules/ConnectorScreen/ConnectorViewSalesforceNew.vue';
//import AllocationBlock from './AllocationBlock.vue';

/* State */
const { mapGetters, mapState, mapActions } = createNamespacedHelpers('dataManagement');
const { mapState: appMapState } = createNamespacedHelpers('app');

export default {

  name: 'CalculationModal',

  components: {
    ContentSpinner,
    ConcatenationPanel,
    AIChatPanel,
    RowFormulaPanel,
    AggregationPanel,
    MdmAggregationPanel,
    LookupPanel,
    //AllocationBlock,
  },

  props: {
    /* Current Table */
    table: {
      default: {},
      type: Object,
    },
  },

  inject: [
    /* Field Calculation data - Modal Show flag. [from TableBuilder] */
    'fieldCalculationData',
    /* Function to close Calculation modal. [from TableBuilder] */
    'hideFieldCalculationModal',
    /* Function update Field in backend. [from TableBuilder] */
    'storeAndRefreshField',
  ],

  data() {
    return {
      /** Component control values. */
      control: {
        loading: true,
        submitting: false,
        /* Apply button */
        applyButtonDisabled: false,
        applyButtonTitle: '',
        /* Clear button */
        clearButtonDisabled: false,
        clearButtonTitle: 'Clear the Calculation details',
      },
      /** Currently selected Field - reference Field provided by TableBuilder. */
      tableField: this.fieldCalculationData.field,
      /** Calculation Data - as per Field.calculation. */
      calculationData: {
        /** Calculation type - value selected in dropdown. */
        fieldCalculationType: '',
      },
      /* Add Entity Utils to 'this' so that they can be referenced in v-if etc.  */
      entityUtils: entityUtils,
    };
  },

  provide() {
    return {
      /** Currently selected Table - reference Field provided by TableBuilder. */
      table: this.table,
      /** Currently selected Field for child components. */
      tableField: this.tableField,
      /** All Fields across all Modules/Folder/Tables. */
      fields: this.fields,
      /** Calculation data for child components. */
      calculationData: this.calculationData,
      /** Clone field with current Calculation properties. */
      constructFieldDefinition: this.constructFieldDefinition,
      /* Utilities */
      apiUtils: apiUtils,
      aiUtils: aiUtils,
      entityUtils: entityUtils,
    };
  },

  watch: {
    ['calculationData']: {
      handler(newValue) {
        /* Check if the apply button should be disabled - if form has a validation failure message. */
        const message = this.formValidationErrorMessage();
        this.control.applyButtonDisabled = !!message;
        this.control.applyButtonTitle = message || 'Store the Field\'s Calculation.';
        this.control.clearButtonDisabled = !this.calculationData.fieldCalculationType;
      },
      deep: true,
      immediate: true,
    },
  },

  computed: {
    ...mapState({
      tables: state => state.tables,
      tablesLoadingState: state => state.tables.loaded,
      fields: state => state.fields,
      //tableField: state => state.builder.tableFieldModal.data,
      fieldValues: state => state.fieldValues,
      calculationModal: state => state.builder.calculationModal,
    }),
    showFormSpinner() {
      return this.control.loading || !this.tablesLoadingState || this.control.submitting;
    },
    formSpinnerText() {
      return this.control.loading || !this.tablesLoadingState ? 'Loading Field Calculation details.  Please wait ...' : 'Storing Field Calculation.  Please wait ...';
    },
    /**
     * Derive the Field Data Type caption to display in the readonly input.
     */
    fieldTypeCaption() {
      return this.entityUtils.dataTypeOptions.find((ft) => ft.value === this.tableField.dataTypeName)?.text;
    },
    /**
     * Get the list of Calculation Types available for this Field.
     * Calculation Type included if:
     * - It is already applied to the Field.
     * - it is allowed for the Field Type and Data Management State (feature control).
     */
    calculationTypeOptions() {
      const fieldCalculationType = this.calculationData.fieldCalculationType;
      const opts = entityUtils.calculationTypeOptions
        .filter(opt =>
          fieldCalculationType == opt.value ||
          entityUtils.fieldAllowsCalculationType(this.tableField, opt.value, this.$store)
        );
      // Ensure the Type currently assigned to the field is in the list.
      if (fieldCalculationType && !opts.find(opt => fieldCalculationType == opt.value))
        opts.push({ value: fieldCalculationType, text: fieldCalculationType });
      return opts;
    },
  },

  methods: {
    ...mapActions([
      'getTables',
      'getFields',
      'updateFields',
    ]),
    /** Load Calculation data from Field, allowing for blank data etc. */
    loadCalculationData() {
      const field = JSON.parse(JSON.stringify(this.tableField));
      const calculationData = entityUtils.extractCalculationDataFromField(field);
      Object.entries(calculationData || {})
        .forEach(([key, value]) => { this.$set(this.calculationData, key, value); });
    },
    /** Clear all properties apart from the Calculation Type. */
    clearCalculationData(clearType = false) {
      Object.keys(this.calculationData)
        .filter((key) => key !== 'fieldCalculationType' || clearType)
        .forEach((key) => this.$delete(this.calculationData, key));
    },
    /** Construction caption for Calculation properties panel (below Calculation Type dropdown). */
    getCalculationTypeCaption() {
      const selectedOption = this.calculationTypeOptions.find(option => option.value === this.calculationData.fieldCalculationType);
      return selectedOption ? selectedOption.text + ' configuration' : 'Select Calculation Type above';
    },
    /** Calculation Type selected in dropdown. */
    onCalculationTypeChanged() {
      this.clearCalculationData();
    },
    /**
     * Determine the (first/primary) reason why the Apply button should be disabled.
     */
    formValidationErrorMessage() {
      return entityUtils.calculationDataValidationFailureMessage(this.calculationData, this.tableField, this.fields.data);
    },
    /**
     * Clone the field definition and update with the current calculation properties.
     * - To be used to verify calculation prior to storing and storing the field on 'Apply'.
     */
    constructFieldDefinition() {
      // Clone field to ensure that nothing sees the changes until saved.
      const field = JSON.parse(JSON.stringify(this.tableField));
      const dataType = entityUtils.getFieldDataType(field);
      const calculationData = this.calculationData;
      const calculationType = calculationData?.fieldCalculationType;
      if (entityUtils.dataTypeIsLookup(dataType) && (calculationType === entityUtils.calculationTypes.lookup || !calculationType)) {
        /*
          Apr25 AH Method of storing Calculation properties differs for Lookup Fields
          but may be brought into line at some stage.
          Ref. entityUtils.fieldHasCalculation().
        */
        if (calculationType) {
          /* Populate Lookup Calculation properties. */
          Object.assign(field.dataTypeJSON, {
            OrderOfCalculation: calculationData.orderOfCalculation,
            SourceRelatedFieldPair: calculationData.sourceRelatedFieldPair || [],
            IsCalculated: true,
          });
        }
        else {
          /* Clear Lookup Calculation properties. */
          Object.assign(field.dataTypeJSON, {
            OrderOfCalculation: 0,
            SourceRelatedFieldPair: [],
            IsCalculated: false,
          });
        }
      }
      else {
        /*
          Standard Field Calculation properties format.
          Construct new version of Field's calculation properties, assigning Calculation Data if type specified.
        */
        field.calculation = calculationType ? calculationData : null;
        field.calculationJSON = calculationType ? jsonUtils.stringifyAsc(field.calculation) : null;
        field.isCalculated = !!calculationType;
      }

      // Clear Field Options where not allowed.
      entityUtils.clearFieldOptionsNotAllowed(field);

      // Serialise dataTypeJSON.
      return { ...field, dataTypeJSON: JSON.stringify(field.dataTypeJSON) };
    },
    /** Store Calculation against Field and close modal. */
    async storeCalculationData() {

      const field = this.constructFieldDefinition();

      /* Store the complete Field definition. */

      // Would like to mimic TableBuilder.updateFieldHandler & TableBuilder.onUpdateFields to avoid updating
      // TableBuilder.onUpdateFields which can be interpretted By FieldItem as 'Field needs saving',
      // but would also need to ensure that TableBuilder is made aware of the changes.
      const response = await this.storeAndRefreshField(field, true);
      if (response === true) {
        this.$toasted.success('Field Calculation updated successfully', { duration: TOASTER_DURATION });
        return true;
      }
      else {
        this.$toasted.error('Unable to update Field Calculation', { duration: TOASTER_DURATION });
        return;
      }
    },
    /** Submit Calculation Data - Store against Field via the backend endpoint. */
    async onSubmit() {
      this.control.submitting = true;
      const success = await this.storeCalculationData();
      if (success) this.hideFieldCalculationModal();
      this.control.submitting = false;
    },
    async onShow() {
    },
  },

  created() {
  },

  /** Component's Reactive State is initialised but DOM not yet rendered. */
  async beforeMount() {
    this.control.loading = true;
    await this.getFields();
    await this.getTables();
    this.loadCalculationData();
    this.control.loading = false;
  },

};

</script>

<style lang="scss" scoped>
#fieldCalculationProperties {
  min-height: 160px;
}

button.mw60 {
  min-width: 60px;
}
</style>
